import { EllipsisVerticalIcon, PenIcon, Trash2Icon } from 'lucide-react';
import type { PropsWithChildren, ReactNode } from 'react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Empty, EmptyContent } from '@/components/ui/empty';
import { formatPercent } from '@/lib/utils';

export const Card = ({ children }: PropsWithChildren) => (
  <div className="bg-card border-border @container rounded-md border p-5">
    {children}
  </div>
);

export const SectionHeader = ({
  actions,
  title
}: {
  actions?: ReactNode;
  title: string;
}) => (
  <div className="flex h-9 items-center justify-between gap-3">
    <h2 className="text-lg font-semibold">{title}</h2>
    {actions}
  </div>
);

export interface TableRow {
  description: null | string;
  id: string;
  name: null | string;
  revenueShare: null | number;
}

export const TableCard = ({
  actions,
  emptyAction,
  onDelete,
  onEdit,
  rows,
  title
}: {
  actions?: ReactNode;
  emptyAction?: ReactNode;
  onDelete?: (id: string) => void;
  onEdit?: (row: TableRow) => void;
  rows: TableRow[];
  title: string;
}) => {
  const showHeaderActions = rows.length !== 0 || !emptyAction;

  return (
    <div className="space-y-2.5">
      <SectionHeader
        actions={showHeaderActions ? actions : undefined}
        title={title}
      />
      {!showHeaderActions && actions && <div className="hidden">{actions}</div>}
      <Card>
        <div className="border-border grid grid-cols-[1fr_1fr_1fr_auto] border-b">
          {['Name', '% of LTM Revenue', 'Description', ''].map(label => (
            <div
              className="px-2 py-3 text-sm font-medium"
              key={label || 'actions'}
            >
              {label}
            </div>
          ))}
        </div>
        {rows.length ? (
          rows.map(row => (
            <div
              className="border-border grid grid-cols-[1fr_1fr_1fr_auto] gap-2 border-b px-2 py-3 text-sm last:border-b-0"
              key={row.id}
            >
              <div>{row.name ?? '-'}</div>
              <div>
                {formatPercent(row.revenueShare, {
                  isDecimal: true,
                  showNullAs: '-'
                })}
              </div>
              <div>{row.description ?? '-'}</div>
              <div>
                {(onEdit || onDelete) && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        size="icon-sm"
                        variant="ghost"
                      >
                        <EllipsisVerticalIcon />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {onEdit && (
                        <DropdownMenuItem
                          onSelect={() => {
                            onEdit(row);
                          }}
                        >
                          <PenIcon />
                          Edit
                        </DropdownMenuItem>
                      )}
                      {onDelete && (
                        <DropdownMenuItem
                          onSelect={() => {
                            onDelete(row.id);
                          }}
                          variant="destructive"
                        >
                          <Trash2Icon />
                          Delete
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            </div>
          ))
        ) : (
          <Empty className="px-0! pt-5! pb-0!">
            <EmptyContent>{emptyAction}</EmptyContent>
          </Empty>
        )}
      </Card>
    </div>
  );
};
